import { useState, useEffect, useCallback } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Icon } from "@/lib/icons";
import { useAuth } from "@/context/AuthContext";
import { Toggle } from "@/components/shared/Toggle";
import { Button, StatusPill, Select } from "@/components/kit";

const dollars = (c) => `$${((c || 0) / 100).toFixed(2)}`;
const REIMB_TONE = { submitted: "gold", approved: "olive", paid: "green", denied: "maroon" };
const SURFACES = ["staff", "garden", "members", "board"];

function readFile(file, cb) {
  const r = new FileReader();
  r.onload = () => cb(r.result);
  r.readAsDataURL(file);
}

const AUD_BOX = "w-full bg-warm-cream border border-ink/15 rounded-lg px-3 py-2 min-h-[40px] text-[14px]";
function AudienceEditor({ kind, cfg, onSaved }) {
  const aud = cfg[kind]?.audience || { tags: [], people: [] };
  const [tags, setTags] = useState([]);
  const [tagSel, setTagSel] = useState("");
  const [q, setQ] = useState(""); const [res, setRes] = useState([]);
  useEffect(() => { api.get("/messages/groups").then((r) => setTags([...(r.data.tags || []), "board", "staff", "all_members"])).catch(() => {}); }, []);
  useEffect(() => { if (q.trim().length < 1) { setRes([]); return; } const t = setTimeout(() => api.get("/messages/recipients/search", { params: { q } }).then((r) => setRes(r.data.people)).catch(() => {}), 200); return () => clearTimeout(t); }, [q]);
  const save = async (next) => { try { const r = await api.put("/reimbursements/config", { [kind]: { ...cfg[kind], audience: next } }); onSaved(r.data); } catch { toast.error("Could not save audience"); } };
  const addTag = (t) => { if (!t || aud.tags.includes(t)) return; save({ ...aud, tags: [...aud.tags, t] }); setTagSel(""); };
  const addPerson = (p) => { if (aud.people.some((x) => x.id === p.user_id)) return; save({ ...aud, people: [...aud.people, { id: p.user_id, name: p.name }] }); setQ(""); setRes([]); };
  return (
    <div className="mt-2 mb-4 pl-1" data-testid={`audience-${kind}`}>
      <div className="text-[12px] font-bold uppercase tracking-wide text-ink/45 mb-1">Who can submit — matches ANY tag/group or is added by name</div>
      <div className="flex flex-wrap gap-1.5 mb-2">
        {aud.tags.map((t) => <StatusPill key={t} tone="olive" className="gap-1">{t}<button data-testid={`aud-${kind}-rmtag-${t}`} onClick={() => save({ ...aud, tags: aud.tags.filter((x) => x !== t) })} className="hover:text-maroon"><Icon name="X" className="w-3 h-3" /></button></StatusPill>)}
        {aud.people.map((p) => <StatusPill key={p.id} tone="gold" className="gap-1">{p.name}<button data-testid={`aud-${kind}-rmperson-${p.id}`} onClick={() => save({ ...aud, people: aud.people.filter((x) => x.id !== p.id) })} className="hover:text-maroon"><Icon name="X" className="w-3 h-3" /></button></StatusPill>)}
        {aud.tags.length === 0 && aud.people.length === 0 && <span className="text-[13px] text-ink/45">Nobody yet — no one will see this form.</span>}
      </div>
      <div className="flex flex-wrap gap-2 items-center">
        <Select data-testid={`aud-${kind}-tag`} value={tagSel} onChange={(e) => addTag(e.target.value)} className={AUD_BOX + " max-w-[200px]"}><option value="">Add a tag or group…</option>{tags.filter((t) => !aud.tags.includes(t)).map((t) => <option key={t} value={t}>{t}</option>)}</Select>
        <div className="relative">
          <input data-testid={`aud-${kind}-person`} value={q} onChange={(e) => setQ(e.target.value)} placeholder="Add a specific person…" className={AUD_BOX + " max-w-[220px]"} />
          {res.length > 0 && <div className="absolute z-10 left-0 right-0 mt-1 bg-cream border border-ink/15 rounded-lg shadow-lg max-h-48 overflow-y-auto">{res.map((p) => <button key={p.user_id} data-testid={`aud-${kind}-personopt-${p.user_id}`} onClick={() => addPerson(p)} className="w-full text-left px-3 py-2 text-[14px] hover:bg-warm-cream border-b border-ink/8 last:border-0">{p.name}</button>)}</div>}
        </div>
      </div>
    </div>
  );
}

function SurfaceToggles({ cfg, onSaved }) {
  return (
    <div className="bg-cream border border-ink/15 rounded-xl p-5 mb-5" data-testid="surface-toggles">
      <h3 className="font-zilla text-lg font-semibold mb-1">Who can submit reimbursements & in-kind</h3>
      <p className="text-[13px] text-ink/60 mb-3">The submission form appears in a member's portal only if they match any tag/group below or are added by name.</p>
      <div className="text-[15px] font-semibold mb-1">Reimbursements</div>
      <AudienceEditor kind="reimbursements" cfg={cfg} onSaved={onSaved} />
      <div className="text-[15px] font-semibold mb-1 border-t border-ink/10 pt-3">In-Kind logging</div>
      <AudienceEditor kind="in_kind" cfg={cfg} onSaved={onSaved} />
    </div>
  );
}

function ReceiptField({ receipt, setReceipt, testid }) {
  return (
    <div>
      <label className="text-[13px] font-semibold block mb-1">Receipt photo (optional)</label>
      {receipt && <img src={receipt} alt="receipt" className="max-h-40 rounded-lg border border-ink/15 mb-2" data-testid={`${testid}-preview`} />}
      <input type="file" accept="image/*" data-testid={testid} onChange={(e) => e.target.files[0] && readFile(e.target.files[0], setReceipt)}
        className="block text-[14px]" />
    </div>
  );
}

function PlotSelect({ plots, value, onChange }) {
  return (
    <div>
      <label className="text-[13px] font-semibold block mb-1">Link a garden plot (optional)</label>
      <Select data-testid="plot-select" value={value} onChange={(e) => onChange(e.target.value)}
        className="w-full bg-warm-cream border border-ink/15 rounded-lg px-3 py-2 min-h-[40px] text-[14px]">
        <option value="">None</option>
        {plots.map((p) => <option key={p.plot_id} value={p.plot_id}>{p.location}</option>)}
      </Select>
    </div>
  );
}

function ReimbForm({ plots, onDone, onClose }) {
  const [f, setF] = useState({ amount: "", description: "", plot_id: "", surface: "staff" });
  const [receipt, setReceipt] = useState("");
  const submit = async () => {
    const cents = Math.round(parseFloat(f.amount || "0") * 100);
    try {
      await api.post("/reimbursements", { amount_cents: cents, description: f.description, plot_id: f.plot_id || null, surface: f.surface, receipt });
      toast.success("Expense submitted"); onDone();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not submit"); }
  };
  return (
    <div className="bg-cream border border-ink/15 rounded-xl p-5 mb-4 space-y-3" data-testid="reimb-form">
      <h3 className="font-zilla text-lg font-semibold">New expense</h3>
      <div className="grid sm:grid-cols-2 gap-3">
        <div><label className="text-[13px] font-semibold block mb-1">Amount</label>
          <input data-testid="reimb-amount" placeholder="$0.00" value={f.amount} onChange={(e) => setF({ ...f, amount: e.target.value })} className="w-full bg-warm-cream border border-ink/15 rounded-lg px-3 py-2 min-h-[40px] text-[14px]" /></div>
        <div><label className="text-[13px] font-semibold block mb-1">Submitting surface</label>
          <Select data-testid="reimb-surface" value={f.surface} onChange={(e) => setF({ ...f, surface: e.target.value })} className="w-full bg-warm-cream border border-ink/15 rounded-lg px-3 py-2 min-h-[40px] text-[14px] capitalize">
            {SURFACES.map((s) => <option key={s} value={s}>{s}</option>)}
          </Select></div>
      </div>
      <div><label className="text-[13px] font-semibold block mb-1">Description</label>
        <textarea data-testid="reimb-description" value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} className="w-full bg-warm-cream border border-ink/15 rounded-lg px-3 py-2 text-[14px]" rows={2} /></div>
      <PlotSelect plots={plots} value={f.plot_id} onChange={(v) => setF({ ...f, plot_id: v })} />
      <ReceiptField receipt={receipt} setReceipt={setReceipt} testid="reimb-receipt" />
      <div className="flex gap-2">
        <Button data-testid="reimb-submit" onClick={submit}>Submit expense</Button>
        <Button variant="secondary" onClick={onClose}>Cancel</Button>
      </div>
    </div>
  );
}

function ReimbDetail({ id, statuses, onClose, onChanged }) {
  const [d, setD] = useState(null);
  const [note, setNote] = useState("");
  const load = useCallback(async () => { const r = await api.get(`/reimbursements/${id}`); setD(r.data); }, [id]);
  useEffect(() => { load(); }, [load]);
  if (!d) return null;
  const setStatus = async (status) => {
    try { const r = await api.put(`/reimbursements/${id}/status`, { status, note }); setD(r.data); setNote(""); onChanged && onChanged(); toast.success(`Marked ${status}`); }
    catch (e) { toast.error("Could not update status"); }
  };
  return (
    <div data-testid="reimb-detail">
      <button onClick={onClose} data-testid="reimb-detail-close" className="inline-flex items-center gap-1.5 text-[14px] font-semibold text-ink/70 hover:text-ink mb-3"><Icon name="ChevronLeft" className="w-4 h-4" /> Back to Review Queue</button>
      <div className="text-[12px] uppercase tracking-widest text-gold font-bold">{dollars(d.amount_cents)}</div>
      <h1 className="font-zilla text-3xl sm:text-4xl font-semibold leading-tight mb-6">{d.description}</h1>
      <div className="max-w-2xl space-y-4">
          <div className="flex items-center gap-2 flex-wrap text-[13px]">
            <StatusPill data-testid="reimb-status" tone={REIMB_TONE[d.status] || "gray"} className="text-[13px] px-3 py-1 capitalize">{d.status}</StatusPill>
            <span className="px-3 py-1 rounded-full bg-cream border border-ink/15 capitalize">{d.surface}</span>
            {d.plot_label && <span className="px-3 py-1 rounded-full bg-olive/15 border border-olive/30">Plot: {d.plot_label}</span>}
          </div>
          <div className="text-[13px] text-ink/60">Submitted by {d.submitted_by?.name || "—"}</div>
          {d.receipt ? <img src={d.receipt} alt="receipt" data-testid="reimb-receipt-img" className="w-full max-h-72 object-contain rounded-lg border border-ink/15 bg-white" /> : <div className="text-[14px] text-ink/50">No receipt attached.</div>}
          <div className="bg-cream border border-ink/15 rounded-xl p-4">
            <h4 className="font-semibold mb-2">Move to status</h4>
            <input data-testid="status-note" placeholder="Note (optional)" value={note} onChange={(e) => setNote(e.target.value)} className="w-full bg-warm-cream border border-ink/15 rounded-lg px-3 py-2 min-h-[40px] text-[14px] mb-2" />
            <div className="flex gap-2 flex-wrap">
              {statuses.map((s) => (
                <Button key={s} variant={s === "denied" ? "danger" : "secondary"} data-testid={`set-status-${s}`} disabled={s === d.status} onClick={() => setStatus(s)} className="capitalize px-4 py-2 min-h-[40px] text-[14px]">{s}</Button>
              ))}
            </div>
          </div>
          <Button variant="danger" className="w-full" data-testid="reimb-remove" onClick={async () => { if (!window.confirm("Remove this expense? It moves to Trash for 30 days.")) return; try { await api.delete(`/reimbursements/${d.reimb_id}`); toast.success("Removed to Trash (30 days)"); onClose(); onChanged && onChanged(); } catch (e) { toast.error(e.response?.data?.detail || "Could not remove"); } }}><Icon name="Trash2" className="w-4 h-4" /> Remove expense</Button>
          <div className="bg-cream border border-ink/15 rounded-xl p-4">
            <h4 className="font-semibold mb-2">History</h4>
            {(d.status_history || []).map((h, i) => (
              <div key={i} className="text-[13px] text-ink/70 flex justify-between border-b border-ink/8 py-1 last:border-0">
                <span className="capitalize">{h.status}{h.note ? ` · ${h.note}` : ""}</span>
                <span className="text-ink/45">{h.by || ""} · {new Date(h.at).toLocaleDateString()}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
  );
}

function InKindForm({ plots, onDone, onClose }) {
  const [f, setF] = useState({ value: "", description: "", plot_id: "", surface: "staff" });
  const [receipt, setReceipt] = useState("");
  const submit = async () => {
    const cents = Math.round(parseFloat(f.value || "0") * 100);
    try {
      await api.post("/in-kind", { estimated_value_cents: cents, description: f.description, plot_id: f.plot_id || null, surface: f.surface, receipt });
      toast.success("Contribution recorded"); onDone();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not record"); }
  };
  return (
    <div className="bg-cream border border-ink/15 rounded-xl p-5 mb-4 space-y-3" data-testid="inkind-form">
      <h3 className="font-zilla text-lg font-semibold">Record an in-kind contribution</h3>
      <div className="grid sm:grid-cols-2 gap-3">
        <div><label className="text-[13px] font-semibold block mb-1">Estimated value</label>
          <input data-testid="inkind-value" placeholder="$0.00" value={f.value} onChange={(e) => setF({ ...f, value: e.target.value })} className="w-full bg-warm-cream border border-ink/15 rounded-lg px-3 py-2 min-h-[40px] text-[14px]" /></div>
        <div><label className="text-[13px] font-semibold block mb-1">Contributing surface</label>
          <Select data-testid="inkind-surface" value={f.surface} onChange={(e) => setF({ ...f, surface: e.target.value })} className="w-full bg-warm-cream border border-ink/15 rounded-lg px-3 py-2 min-h-[40px] text-[14px] capitalize">
            {SURFACES.map((s) => <option key={s} value={s}>{s}</option>)}
          </Select></div>
      </div>
      <div><label className="text-[13px] font-semibold block mb-1">What was purchased</label>
        <textarea data-testid="inkind-description" value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} className="w-full bg-warm-cream border border-ink/15 rounded-lg px-3 py-2 text-[14px]" rows={2} /></div>
      <PlotSelect plots={plots} value={f.plot_id} onChange={(v) => setF({ ...f, plot_id: v })} />
      <ReceiptField receipt={receipt} setReceipt={setReceipt} testid="inkind-receipt" />
      <div className="flex gap-2">
        <Button variant="olive" data-testid="inkind-submit" onClick={submit}>Record contribution</Button>
        <Button variant="secondary" onClick={onClose}>Cancel</Button>
      </div>
    </div>
  );
}

export default function ReviewQueue() {
  const { me } = useAuth();
  const [tab, setTab] = useState("reimbursements");
  const [cfg, setCfg] = useState(null);
  const [plots, setPlots] = useState([]);
  const [reimb, setReimb] = useState({ items: [], total: 0, by_status: {}, statuses: [] });
  const [inkind, setInkind] = useState({ items: [], total: 0, total_estimated_value_cents: 0 });
  const [statusFilter, setStatusFilter] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [openId, setOpenId] = useState(null);

  const loadReimb = useCallback(async () => {
    const r = await api.get("/reimbursements", { params: statusFilter ? { status: statusFilter } : {} });
    setReimb(r.data);
  }, [statusFilter]);
  const loadInkind = useCallback(async () => { const r = await api.get("/in-kind"); setInkind(r.data); }, []);
  useEffect(() => {
    api.get("/reimbursements/config").then((r) => setCfg(r.data)).catch(() => {});
    api.get("/garden-plots", { params: { limit: 100 } }).then((r) => setPlots(r.data.plots || [])).catch(() => {});
  }, []);
  useEffect(() => { loadReimb(); }, [loadReimb]);
  useEffect(() => { loadInkind(); }, [loadInkind]);

  if (openId) return <ReimbDetail id={openId} statuses={reimb.statuses} onClose={() => setOpenId(null)} onChanged={loadReimb} />;

  return (
    <div data-testid="review-queue-page">
      <h1 className="font-zilla text-3xl sm:text-4xl font-semibold mb-1">Reimbursements &amp; In-Kind</h1>
      <p className="text-[16px] text-ink/70 mb-5">A shared RCA expense queue for anyone — gardeners, staff, and board — plus documented in-kind contributions.</p>

      {me?.user?.is_super_admin && cfg && <SurfaceToggles cfg={cfg} onSaved={setCfg} />}

      <div className="flex gap-2 mb-4" data-testid="rq-tabs">
        <button data-testid="tab-reimbursements" onClick={() => { setTab("reimbursements"); setShowForm(false); }} className={`rounded-lg px-4 py-2 min-h-[42px] text-[15px] font-semibold ${tab === "reimbursements" ? "bg-ink text-cream" : "border border-ink/20"}`}>Reimbursements</button>
        <button data-testid="tab-inkind" onClick={() => { setTab("in_kind"); setShowForm(false); }} className={`rounded-lg px-4 py-2 min-h-[42px] text-[15px] font-semibold ${tab === "in_kind" ? "bg-ink text-cream" : "border border-ink/20"}`}>In-Kind</button>
      </div>

      {tab === "reimbursements" ? (
        <div>
          <div className="flex items-center justify-between gap-2 mb-3 flex-wrap">
            <Select data-testid="reimb-status-filter" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="bg-warm-cream border border-ink/15 rounded-lg px-3 py-2 min-h-[42px] text-[14px] capitalize">
              <option value="">All statuses</option>
              {reimb.statuses.map((s) => <option key={s} value={s}>{s}</option>)}
            </Select>
            <Button data-testid="new-reimb-btn" onClick={() => setShowForm((v) => !v)}><Icon name="Plus" className="w-4 h-4" /> New expense</Button>
          </div>
          {showForm && <ReimbForm plots={plots} onClose={() => setShowForm(false)} onDone={() => { setShowForm(false); loadReimb(); }} />}
          <div className="bg-cream border border-ink/15 rounded-xl overflow-hidden" data-testid="reimb-list">
            {reimb.items.length === 0 && <div className="p-10 text-center text-ink/55">No expenses yet.</div>}
            {reimb.items.map((r) => (
              <button key={r.reimb_id} data-testid={`reimb-row-${r.reimb_id}`} onClick={() => setOpenId(r.reimb_id)} className="w-full flex items-center justify-between gap-3 px-5 py-4 border-b border-ink/8 last:border-0 text-left hover:bg-warm-cream/60">
                <div className="min-w-0">
                  <div className="text-[16px] font-semibold flex items-center gap-2">{dollars(r.amount_cents)}<span className="text-[13px] text-ink/55 font-normal truncate">{r.description}</span></div>
                  <div className="text-[13px] text-ink/55 capitalize">{r.surface}{r.plot_label ? ` · ${r.plot_label}` : ""}{r.has_receipt ? " · 📎" : ""}</div>
                </div>
                <StatusPill tone={REIMB_TONE[r.status] || "gray"} className="text-[12px] px-2.5 py-1 capitalize shrink-0">{r.status}</StatusPill>
              </button>
            ))}
          </div>
        </div>
      ) : (
        <div>
          <div className="bg-gold/15 border border-gold/40 rounded-lg p-4 mb-4 text-[15px] text-ink/80" data-testid="inkind-banner">
            This is <span className="font-semibold">documentation only — not a payment request</span>. Use it to record purchases made on RCA's behalf where no reimbursement is being sought.
          </div>
          <div className="bg-dark-olive text-cream rounded-xl p-5 mb-4 flex items-center justify-between" data-testid="inkind-total">
            <div><div className="text-[13px] uppercase tracking-widest text-gold font-bold">Total estimated in-kind value</div>
              <div className="text-[12px] text-cream/70">Ready for grant reporting</div></div>
            <div className="font-zilla text-3xl font-semibold" data-testid="inkind-total-value">{dollars(inkind.total_estimated_value_cents)}</div>
          </div>
          <div className="flex justify-end mb-3">
            <Button data-testid="new-inkind-btn" onClick={() => setShowForm((v) => !v)}><Icon name="Plus" className="w-4 h-4" /> Record a contribution</Button>
          </div>
          {showForm && <InKindForm plots={plots} onClose={() => setShowForm(false)} onDone={() => { setShowForm(false); loadInkind(); }} />}
          <div className="bg-cream border border-ink/15 rounded-xl overflow-hidden" data-testid="inkind-list">
            {inkind.items.length === 0 && <div className="p-10 text-center text-ink/55">No contributions logged yet.</div>}
            {inkind.items.map((r) => (
              <div key={r.inkind_id} data-testid={`inkind-row-${r.inkind_id}`} className="flex items-center justify-between gap-3 px-5 py-4 border-b border-ink/8 last:border-0">
                <div className="min-w-0">
                  <div className="text-[16px] font-semibold truncate">{r.description}</div>
                  <div className="text-[13px] text-ink/55 capitalize">{r.surface}{r.plot_label ? ` · ${r.plot_label}` : ""} · by {r.recorded_by?.name || "—"}{r.has_receipt ? " · 📎" : ""}</div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <span className="text-[15px] font-semibold">{dollars(r.estimated_value_cents)}</span>
                  <button data-testid={`inkind-remove-${r.inkind_id}`} onClick={async () => { if (!window.confirm("Remove this contribution? It moves to Trash for 30 days.")) return; try { await api.delete(`/in-kind/${r.inkind_id}`); toast.success("Removed to Trash (30 days)"); loadInkind(); } catch (e) { toast.error(e.response?.data?.detail || "Could not remove"); } }} className="text-ink/40 hover:text-maroon"><Icon name="Trash2" className="w-4 h-4" /></button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

    </div>
  );
}
